<div class="content-info">
  <div class="content-title">
    <h3 class="title">Stock Movement</h3>
  </div>

  <form class="sm-filters" (ngSubmit)="onSearch()">
    <div class="sm-field">
      <label for="skuSearch" class="form-label">Search by SKU</label>
      <ng-select
        id="skuSearch"
        name="selectedProduct"
        [items]="products"
        bindLabel="sku"
        [typeahead]="productSearch$"
        [loading]="isSearchingProduct"
        [minTermLength]="3"
        [clearable]="true"
        placeholder="Type 3+ chars"
        [(ngModel)]="searchParams.selectedProduct"
        (change)="onProductSelect($event)"
      >
        <ng-template ng-option-tmp let-item="item">
          <small><strong>{{ item.sku }}</strong> - {{ item.name }}</small>
        </ng-template>
        <ng-template ng-label-tmp let-item="item">
          <span class="sm-mono">{{ item.sku }}</span>
        </ng-template>
        <ng-template ng-notfound-tmp> No SKU found. </ng-template>
      </ng-select>
    </div>

    <div class="sm-field">
      <label for="eanSearch" class="form-label">Search by EAN</label>
      <ng-select
        id="eanSearch"
        name="selectedProductByEan"
        [items]="eanSearchResults"
        bindLabel="ean"
        [typeahead]="productEanSearch$"
        [loading]="isSearchingByEan"
        [minTermLength]="3"
        [clearable]="true"
        placeholder="Type 3+ digits"
        [(ngModel)]="searchParams.selectedProduct"
        (change)="onProductSelect($event)"
      >
        <ng-template ng-option-tmp let-item="item">
          <small><strong>{{ item.ean }}</strong> - {{ item.sku }} - {{ item.name }}</small>
        </ng-template>
        <ng-template ng-label-tmp let-item="item">
          <span class="sm-mono">{{ item.ean || item.sku }}</span>
        </ng-template>
        <ng-template ng-notfound-tmp> No EAN found. </ng-template>
      </ng-select>
    </div>

    <div class="sm-field sm-field--wide">
      <label for="nameSearch" class="form-label">Product name</label>
      <ng-select
        id="nameSearch"
        name="selectedProductByName"
        [items]="nameSearchResults"
        bindLabel="name"
        [typeahead]="productNameSearch$"
        [loading]="isSearchingByName"
        [minTermLength]="3"
        [clearable]="true"
        placeholder="Type to search"
        [(ngModel)]="searchParams.selectedProduct"
        (change)="onProductSelect($event)"
      >
        <ng-template ng-option-tmp let-item="item">
          <small>{{ item.name }}</small>
        </ng-template>
        <ng-template ng-label-tmp let-item="item">
          <span class="sm-ellipsis" [title]="item.name">{{ item.name }}</span>
        </ng-template>
        <ng-template ng-notfound-tmp> No Product found. </ng-template>
      </ng-select>
    </div>

    <div class="sm-field sm-field--wide">
      <label for="storeSelect" class="form-label">Store</label>
      <ng-select
        id="storeSelect"
        name="selectedStores"
        class="sm-store-select"
        [items]="stores"
        bindLabel="name"
        [multiple]="true"
        [clearable]="true"
        [searchable]="true"
        [closeOnSelect]="false"
        placeholder="All stores"
        [(ngModel)]="searchParams.selectedStores"
      >
        <ng-template ng-option-tmp let-item="item" let-item$="item$">
          <input type="checkbox" class="form-check-input me-2" [ngModel]="item$.selected" [ngModelOptions]="{standalone: true}"/> {{item.name}}
        </ng-template>
        <ng-template ng-multi-label-tmp let-items="items">
          <div class="ng-value sm-store-summary" [title]="storeNames(items)">
            <span class="sm-ellipsis">{{ items[0]?.name }}</span>
            <span *ngIf="items.length > 1" class="sm-more">+{{ items.length - 1 }} more</span>
          </div>
        </ng-template>
      </ng-select>
    </div>

    <div class="sm-field">
      <label for="fromDate" class="form-label">From date</label>
      <input
        type="date"
        id="fromDate"
        name="fromDate"
        class="form-control"
        [(ngModel)]="searchParams.fromDate"
        required
      />
    </div>

    <div class="sm-field">
      <label for="toDate" class="form-label">To date</label>
      <input
        type="date"
        id="toDate"
        name="toDate"
        class="form-control"
        [(ngModel)]="searchParams.toDate"
        required
      />
    </div>

    <div class="sm-field sm-field--action">
      <button class="btn btn-primary sm-search-btn" type="submit" [disabled]="loading">
        <span *ngIf="loading" class="spinner-border spinner-border-sm" role="status"></span>
        <ng-container *ngIf="!loading"><i class="bi bi-search me-1"></i>Search</ng-container>
      </button>
    </div>
  </form>

  <!-- Header Summary Section -->
  <div *ngIf="meta" class="card mb-3 bg-light border-0">
    <div class="card-body">
      <div class="row">
        <div class="col-md-4">
          <p class="mb-1"><strong>Product Name:</strong> {{ meta.productName }}</p>
          <p class="mb-1"><strong>Location:</strong> {{ meta.location }}</p>
        </div>
        <div class="col-md-4">
          <p class="mb-1"><strong>SKU:</strong> {{ meta.sku }}</p>
          <p class="mb-1"><strong>From Date:</strong> {{ meta.fromDate | date:'dd-MM-yyyy' }}</p>
        </div>
        <div class="col-md-4">
          <p class="mb-1"><strong>Total Available Stock:</strong> {{ meta.totalAvailableStock | number : "1.0-3" }}</p>
          <p class="mb-1"><strong>To Date:</strong> {{ meta.toDate | date:'dd-MM-yyyy' }}</p>
        </div>
      </div>
    </div>
  </div>

  <div class="common-table">
    <div *ngIf="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2">Fetching stock movements...</p>
    </div>

    <div *ngIf="!loading && movementList.length === 0" class="sm-empty">
      <div class="sm-empty__icon"><i class="bi bi-arrow-left-right"></i></div>
      <div class="sm-empty__title">No stock movement found</div>
      <div class="sm-empty__text">Pick a product, adjust the stores or date range, and hit Search.</div>
    </div>

    <div *ngIf="!loading && movementList.length > 0" class="table-responsive">
      <table class="table table-bordered align-middle text-center table-hover">
        <thead class="table-light">
          <tr>
            <th rowspan="2" class="align-middle">Date</th>
            <th rowspan="2" class="align-middle">Opening Stock</th>
            <th colspan="6" class="bg-success-subtle text-success-emphasis">Inward</th>
            <th colspan="7" class="bg-danger-subtle text-danger-emphasis">Outward</th>
            <th rowspan="2" class="align-middle">Closing Stock</th>
          </tr>
          <tr>
            <!-- Inward Sub-headers -->
            <th class="bg-success-subtle text-success-emphasis">GRN</th>
            <th class="bg-success-subtle text-success-emphasis">Transfer In</th>
            <th class="bg-success-subtle text-success-emphasis">Bulk Breakup Input</th>
            <th class="bg-success-subtle text-success-emphasis">Sales Return</th>
            <th class="bg-success-subtle text-success-emphasis">Stock Take (+)</th>
            <th class="bg-success-subtle text-success-emphasis">Adjustment (+)</th>

            <!-- Outward Sub-headers -->
            <th class="bg-danger-subtle text-danger-emphasis">Transfer Out</th>
            <th class="bg-danger-subtle text-danger-emphasis">Billing</th>
            <th class="bg-danger-subtle text-danger-emphasis">PRN</th>
            <th class="bg-danger-subtle text-danger-emphasis">Write Off</th>
            <th class="bg-danger-subtle text-danger-emphasis">Bulk Breakup Output</th>
            <th class="bg-danger-subtle text-danger-emphasis">Stock Take (−)</th>
            <th class="bg-danger-subtle text-danger-emphasis">Adjustment (−)</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of movementList">
            <td>{{ item.date | date : "dd-MM-yyyy" }}</td>
            <td class="fw-bold">{{ item.openingStock | number : "1.0-3" }}</td>
            
            <!-- Inward -->
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.grn.qty > 0"
                (click)="openDetailPopup(item.inward.grn.items, 'GRN')"
              >
                {{ item.inward.grn.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.grn.qty <= 0">0</span>
            </td>
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.transfer.qty > 0"
                (click)="openDetailPopup(item.inward.transfer.items, 'Transfer In')"
              >
                {{ item.inward.transfer.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.transfer.qty <= 0">0</span>
            </td>
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.repackInput.qty > 0"
                (click)="openDetailPopup(item.inward.repackInput.items, 'Bulk Breakup Input')"
              >
                {{ item.inward.repackInput.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.repackInput.qty <= 0">0</span>
            </td>
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.salesReturn.qty > 0"
                (click)="openDetailPopup(item.inward.salesReturn.items, 'Sales Return')"
              >
                {{ item.inward.salesReturn.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.salesReturn.qty <= 0">0</span>
            </td>
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.stockTake.qty > 0"
                (click)="openDetailPopup(item.inward.stockTake.items, 'Stock Take (In)')"
              >
                {{ item.inward.stockTake.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.stockTake.qty <= 0">0</span>
            </td>
            <td class="text-success">
              <a
                href="javascript:void(0)"
                *ngIf="item.inward.other.qty > 0"
                (click)="openDetailPopup(item.inward.other.items, 'Adjustment (In)')"
              >
                {{ item.inward.other.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.inward.other.qty <= 0">0</span>
            </td>

            <!-- Outward -->
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.transfer.qty > 0"
                (click)="openDetailPopup(item.outward.transfer.items, 'Transfer Out')"
              >
                {{ item.outward.transfer.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.transfer.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.billing.qty > 0"
                (click)="openDetailPopup(item.outward.billing.items, 'Billing')"
              >
                {{ item.outward.billing.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.billing.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.prn.qty > 0"
                (click)="openDetailPopup(item.outward.prn.items, 'PRN')"
              >
                {{ item.outward.prn.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.prn.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.writeOff.qty > 0"
                (click)="openDetailPopup(item.outward.writeOff.items, 'Write Off')"
              >
                {{ item.outward.writeOff.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.writeOff.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.repackOutput.qty > 0"
                (click)="openDetailPopup(item.outward.repackOutput.items, 'Bulk Breakup Output')"
              >
                {{ item.outward.repackOutput.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.repackOutput.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.stockTake.qty > 0"
                (click)="openDetailPopup(item.outward.stockTake.items, 'Stock Take (Out)')"
              >
                {{ item.outward.stockTake.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.stockTake.qty <= 0">0</span>
            </td>
            <td class="text-danger">
              <a
                href="javascript:void(0)"
                *ngIf="item.outward.other.qty > 0"
                (click)="openDetailPopup(item.outward.other.items, 'Adjustment (Out)')"
              >
                {{ item.outward.other.qty | number : "1.0-3" }}
              </a>
              <span *ngIf="item.outward.other.qty <= 0">0</span>
            </td>

            <td class="fw-bold">{{ item.closingStock | number : "1.0-3" }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div *ngIf="!loading && movementList.length > 0" class="table-pagination mt-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="pagination-count">
            Rows per page:
          <select [(ngModel)]="pageSize" (change)="pageSizeChange()" class="form-select d-inline-block w-auto ms-2">
            <option [value]="10">10</option>
            <option [value]="20">20</option>
            <option [value]="30">30</option>
          </select>
        </div>

        <ul class="pagination mb-0">
          <li
            class="page-item"
            [class.disabled]="currentPage === 1"
          >
            <button class="page-link" (click)="goToPage(currentPage - 1)">Prev</button>
          </li>

          <li
            class="page-item"
            *ngFor="let p of getPageNumbers()"
            [class.active]="p === currentPage"
          >
            <button class="page-link" (click)="goToPage(p)">{{ p }}</button>
          </li>

          <li
            class="page-item"
            [class.disabled]="currentPage === pageCount"
          >
            <button class="page-link" (click)="goToPage(currentPage + 1)">Next</button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</div>

<!-- Details Modal -->
<div
  class="modal fade"
  [class.show]="showModal"
  [style.display]="showModal ? 'block' : 'none'"
  tabindex="-1"
  role="dialog"
  aria-labelledby="detailsModalLabel"
  aria-hidden="true"
  style="background-color: rgba(0, 0, 0, 0.5)"
  (click)="closeDetailPopup()"
>
  <div class="modal-dialog modal-lg modal-dialog-centered" role="document" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="detailsModalLabel">{{ selectedCategory }} Details</h5>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          (click)="closeDetailPopup()"
        ></button>
      </div>
      <div class="modal-body">
        <div class="table-responsive">
          <table class="table table-striped table-bordered text-center align-middle">
            <thead class="table-light">
              <tr>
                <th>Ref Number</th>
                <th>{{ selectedCategory === 'Transfer Out' ? 'To Store' : 'Store' }}</th>
                <th>Date</th>
                <th>Qty</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let detail of selectedDetails">
                <td>{{ detail.refNo || '-' }}</td>
                <td>{{ detail.party || '-' }}</td>
                <td>{{ detail.date | date : "medium" }}</td>
                <td>{{ detail.qty | number : "1.0-3" }}</td>
                <td>{{ detail.notes || '-' }}</td>
              </tr>
              <tr *ngIf="selectedDetails.length === 0">
                <td colspan="5" class="text-center text-muted">No details available.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDetailPopup()">
          Close
        </button>
      </div>
    </div>
  </div>
</div>
